import React, { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router'
import {
  Card,
  CardHeader,
  CardContent,
  CardActionArea,
  CircularProgress,
  Grid,
  Divider,
  Typography,
  Switch,
  IconButton,
  Link,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  withStyles,
  useTheme,
  useMediaQuery
} from '@material-ui/core'
import moment from 'moment'
import localization from 'moment/locale/pt-br'

// Icons
import AdjustIcon from '@material-ui/icons/Adjust'
import BusinessIcon from '@material-ui/icons/Business'
import PersonIcon from '@material-ui/icons/Person'
import BookmarkIcon from '@material-ui/icons/Bookmark'
import LayersIcon from '@material-ui/icons/Layers'
import FolderIcon from '@material-ui/icons/Folder'
import DescriptionIcon from '@material-ui/icons/Description'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import CreateIcon from '@material-ui/icons/Create'
import CloudDownloadIcon from '@material-ui/icons/CloudDownload'

// Hooks and Services
import { useVestigioService } from 'hooks/useVestigioService'
import { useCadaverService } from 'hooks/useCadaverService'
import { useVeiculoService } from 'hooks/useVeiculoService'
import { usePericiandoService } from 'hooks/usePericiandoService'
import { useCestaOperacaoLote } from 'hooks/useCestaOperacaoLote'
import { useAuth } from 'hooks/useAuth'
import useRouter from 'utils/useRouter'
import constants from 'utils/constants'
import { getLabelStatusCadeiaDeCustodia } from 'helpers/labels'

// Shared Components
import { Page, Header, ObjetoAvatar, SecaoGenerica } from 'components'
import { ExibirArquivo } from 'components/ExibirArquivo'
import { ConfirmarIdentidade } from 'components/ConfirmarIdentidade'
import { FAV } from 'components/FAV'
import { TimeLineCadeiaDeCustodia } from 'components/TimeLineCadeiaDeCustodia'
import { CardExameSolicitado } from 'components/Cards/CardExameSolicitado'
import { CardCaso } from 'components/Cards/CardCaso'
import { CardCadaver } from 'components/Cards/CardCadaver'
import { CardVestigio } from 'components/Cards/CardVestigio'
import { CardVeiculo } from 'components/Cards/CardVeiculo'
import { CardPericiando } from 'components/Cards/CardPericiando'
import { ListaInformacoes } from 'views/Informacao/ListaInformacoes'
import { HistoricoOperacoes } from 'components/HistoricoOperacoes'


import { MenuAcoes } from './components/MenuAcoes/MenuAcoes'
import { useStyles } from './Vestigio.styles'

const StyledMenu = withStyles({
  paper: {
    border: '1px solid #d3d4d5',
  },
})((props) => (
  <Menu
    elevation={0}
    getContentAnchorEl={null}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'center',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'center',
    }}
    {...props}
  />
))

const StyledMenuItem = withStyles((theme) => ({
  root: {
    '&:focus': {
      backgroundColor: theme.palette.secondary.main,
      '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
        color: theme.palette.common.white,
      },
    },
  },
}))(MenuItem)

export const Vestigio = (props) => {
  const { route } = props
  moment.locale('pt-br', localization)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))
  const classes = useStyles()
  const router = useRouter()
  const params = useParams()

  const vestigioService = useVestigioService()
  const cadaverService = useCadaverService()
  const veiculoService = useVeiculoService()
  const periciandoService = usePericiandoService()
  const { reloadListeners } = useCestaOperacaoLote()
  const { currentUser, verificarPermissao } = useAuth()
  const usuarioState = currentUser

  // Estados dos Dados e Loading
  const [dados, setDados] = useState(null)
  const [vestigioOrigem, setVestigioOrigem] = useState(null)
  const [cadaverOrigem, setCadaverOrigem] = useState(null)
  const [veiculoOrigem, setVeiculoOrigem] = useState(null)
  const [periciandoOrigem, setPericiandoOrigem] = useState(null)
  const [vestigiosExtraidos, setVestigiosExtraidos] = useState(null)
  const [entregasVestigio, setEntregasVestigio] = useState(null)
  const [informacoesAdicionadas, setInformacoesAdicionadas] = useState(null)
  const [termoNaoConformidade, setTermoNaoConformidade] = useState(null)
  const [carregando, setCarregando] = useState(false)

  // Estados Locais de Ações
  const [switchCadeiaCustodia, setSwitchCadeiaCustodia] = useState(false)
  const [openConfirmarIdentidade, setOpenConfirmarIdentidade] = useState(false)
  const [anchorElDocumento, setAnchorElDocumento] = useState(null)
  const [processando, setProcessando] = useState(false)
  const [isTermoAssinado, setIsTermoAssinado] = useState(false)
  const [ultimaOperacaoGuardar, setUltimaOperacaoGuardar] = useState(null)

  const usuarioPertenceAoSetor = currentUser?.setores?.some((setorDoUsuario) => setorDoUsuario.id === dados?.setAtual?.id)

  const fetchDados = useCallback(async () => {
    setVestigioOrigem(null)
    setCadaverOrigem(null)
    setVeiculoOrigem(null)
    setPericiandoOrigem(null)
    setVestigiosExtraidos(null)
    setEntregasVestigio(null)
    setInformacoesAdicionadas(null)
    setTermoNaoConformidade(null)
    setCarregando(true)

    const vestigio = await vestigioService.listarVestigioDetalhado(params.id)
    if (vestigio) {
      setDados(vestigio)
      setCarregando(false)

      if (vestigio.id_vestigio_origem) {
        const vestOrig = await vestigioService.listarVestigioAsync(vestigio.id_vestigio_origem)
        setVestigioOrigem(vestOrig)
      }

      if (vestigio.id_cadaver_origem) {
        const cadavOrig = await cadaverService.listarCadaverAsync(vestigio.id_cadaver_origem)
        setCadaverOrigem(cadavOrig)
      }

      if (vestigio.id_veiculo_origem) {
        const veicOrig = await veiculoService.listarVeiculoAsync(vestigio.id_veiculo_origem)
        setVeiculoOrigem(veicOrig)
      }

      if (vestigio.id_periciando_origem) {
        const pericOrig = await periciandoService.listarPericiandoAsync(vestigio.id_periciando_origem)
        setPericiandoOrigem(pericOrig)
      }

      const extraidos = await vestigioService.listarVestigiosExtraidosDeVestigioAsync(vestigio.id)
      setVestigiosExtraidos(extraidos)

      const entregas = await vestigioService.listarEntregasVestigio(vestigio.id)
      setEntregasVestigio(entregas)

      const informacoes = await vestigioService.listarInformacoesAdicionadas(vestigio.id)
      setInformacoesAdicionadas(informacoes)

      const termo = await vestigioService.listarTermoNaoConformidade(vestigio.id)
      setTermoNaoConformidade(termo)
    } else {
      setCarregando(false)
    }
  }, [params.id, reloadListeners])

  useEffect(() => {
    if (params.id) {
      fetchDados()
    }
  }, [fetchDados, params.id])

  useEffect(() => {
    const fetchUltimaOperacaoGuardar = async () => {
      const response = await vestigioService.listarUltimaOperacaoGuardarAsync(dados.id)
      setUltimaOperacaoGuardar(response)
    }
    if (dados?.id) {
      fetchUltimaOperacaoGuardar()
    }
  }, [dados])

  const handleChangeSwitchCadeiaCustodia = (event) => {
    setSwitchCadeiaCustodia(event.target.checked)
  }

  const handleClickExameSolicitado = (id) => {
    router.history.push(`/casos/caso/solicitacao/exameSolicitado/detalhar/${id}`)
  }

  const handleClickCaso = (id, novaAba) => {
    if (novaAba) {
      window.open(`/casos/caso/detalhar/${id}`, '_blank', 'noreferrer')
    } else {
      router.history.push(`/casos/caso/detalhar/${id}`)
    }
  }

  const handleClickCadaver = (id) => {
    router.history.push(`/casos/caso/cadaver/detalhar/${id}`)
  }

  const handleClickPericiando = (id) => {
    router.history.push(`/casos/caso/periciando/detalhar/${id}`)
  }

  const handleClickVestigio = (id, novaTab) => {
    if (novaTab) {
      window.open(`/casos/caso/vestigio/detalhar/${id}`, '_blank', 'noreferrer')
    } else {
      router.history.push(`/casos/caso/vestigio/detalhar/${id}`)
    }
  }

  const handleClickVeiculo = (id) => {
    router.history.push(`/casos/caso/veiculo/detalhar/${id}`)
  }

  const handleClickOpenConfirmarIdentidade = () => {
    setOpenConfirmarIdentidade(true)
  }

  const isUsuarioAssinante = (idUsuario) => {
    if (!idUsuario || !termoNaoConformidade) {
      return false
    }
    if (
      (idUsuario === termoNaoConformidade.id_usuario_testemunha && !termoNaoConformidade.testemunha_assinatura) ||
      (idUsuario === termoNaoConformidade.id_perito && !termoNaoConformidade.perito_assinatura)
    ) {
      return true
    }
    return false
  }

  const handleDownloadTermoNaoConformidade = async () => {
    handleCloseMenuDocumento()
    await vestigioService.downloadPDFTermoNaoConformidade(termoNaoConformidade.id)
  }

  const verificarTermoAssinado = useCallback(() => {
    if (termoNaoConformidade == null) {
      setIsTermoAssinado(false)
    } else if (termoNaoConformidade.testemunha_assinatura && termoNaoConformidade.perito_assinatura) {
      setIsTermoAssinado(true)
    } else {
      setIsTermoAssinado(false)
    }
  }, [termoNaoConformidade])

  useEffect(() => {
    verificarTermoAssinado()
  }, [verificarTermoAssinado])

  const handleClickAssinarDocumento = async () => {
    handleCloseMenuDocumento()
    await vestigioService.assinarTermoNaoConformidade(termoNaoConformidade.id, setProcessando)
    fetchDados()
  }

  const handleCloseMenuDocumento = () => {
    setAnchorElDocumento(null)
  }

  const handleClickBotaoAcoesDocumento = (event) => {
    setAnchorElDocumento(event.currentTarget)
  }

  const criarLote = useCallback(async (id) => {
    setCarregando(true)
    const response = await vestigioService.criarLote(id)
    router.history.push(`/vestigios/lote/${response.id}`)
    setCarregando(false)
  }, [])

  return (
    <Page className={classes.root} title={`${route.secao} - SIGEP`}>
      <Header titulo={`${route.secao}`} overline={route.secao} />

      <div className={classes.resultsWrapper}>
        {carregando || !dados ? (
          <div className={classes.carregando}>
            <CircularProgress disableShrink />
          </div>
        ) : (
          <>
            {!carregando && (
              <MenuAcoes
                fetchDados={fetchDados}
                vestigio={dados}
                setProcessando={setCarregando}
                ultimaOperacao={ultimaOperacaoGuardar}
                apenasCustodia={true}
              />
            )}

            <Card className={classes.headerCard}>
              <CardContent style={{ padding: '24px' }}>
                <Grid container spacing={3} alignItems="center" justifyContent="space-between">
                  <Grid item xs={12} sm={6} md={7} style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <ObjetoAvatar tipo="VESTIGIO" />
                    <Typography variant="h3" className={classes.caseTitle}>
                      {dados.identificador?.toUpperCase()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={5} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {!carregando && (
                      <MenuAcoes
                        fetchDados={fetchDados}
                        vestigio={dados}
                        setProcessando={setCarregando}
                        ultimaOperacao={ultimaOperacaoGuardar}
                        apenasMenu={true}
                      />
                    )}
                  </Grid>
                </Grid>

                <Divider style={{ margin: '20px 0 24px 0' }} />

                <Typography variant="subtitle1" className={classes.caseSubtitle} style={{ marginTop: 0, marginBottom: '24px' }}>
                  {dados.descricao?.toUpperCase() || 'SEM DESCRIÇÃO ADICIONADA'}
                </Typography>


                <Grid container spacing={3} className={classes.metaGrid} alignItems="stretch">
                  <Grid item xs={12} sm={6} md={4}>
                    <div className={classes.metaItem}>
                      <AdjustIcon className={classes.metaIcon} />
                      <div className={classes.metaTextContainer}>
                        <Typography className={classes.metaLabel}>STATUS</Typography>
                        <Typography className={classes.metaValue}>
                          {getLabelStatusCadeiaDeCustodia(dados.statusCadeiaCustodia)?.toUpperCase()}
                        </Typography>
                      </div>
                    </div>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <div className={classes.metaItem}>
                      <BusinessIcon className={classes.metaIcon} />
                      <div className={classes.metaTextContainer}>
                        <Typography className={classes.metaLabel}>SETOR ATUAL</Typography>
                        <Typography className={classes.metaValue}>
                          {dados.setAtual ? `${dados.setAtual.nome?.toUpperCase()} - ${dados.setAtual.divisao?.sigla?.toUpperCase()}` : '-'}
                        </Typography>
                      </div>
                    </div>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <div className={classes.metaItem}>
                      <PersonIcon className={classes.metaIcon} />
                      <div className={classes.metaTextContainer}>
                        <Typography className={classes.metaLabel}>USUÁRIO ATUAL</Typography>
                        <Typography className={classes.metaValue}>
                          {dados.usuAtual ? dados.usuAtual.nome?.toUpperCase() : '-'}
                        </Typography>
                      </div>
                    </div>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <div className={classes.metaItem}>
                      <BookmarkIcon className={classes.metaIcon} />
                      <div className={classes.metaTextContainer}>
                        <Typography className={classes.metaLabel}>LACRE ATUAL</Typography>
                        <Typography className={classes.metaValue}>
                          {dados.lacre || '-'}
                        </Typography>
                      </div>
                    </div>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <div className={classes.metaItem}>
                      <LayersIcon className={classes.metaIcon} />
                      <div className={classes.metaTextContainer}>
                        <Typography className={classes.metaLabel}>UNIDADES</Typography>
                        <Typography className={classes.metaValue}>
                          {dados.quantidade || '0'}
                        </Typography>
                      </div>
                    </div>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <div className={classes.metaItem}>
                      <FolderIcon className={classes.metaIcon} />
                      <div className={classes.metaTextContainer}>
                        <Typography className={classes.metaLabel}>LOTE</Typography>
                        <Typography className={classes.metaValue}>
                          {dados.lote ? (
                            <Link style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => router.history.push(`/vestigios/lote/${dados.lote.id}`)}>
                              {dados.lote.numero}
                            </Link>
                          ) : (
                            <Link style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => criarLote(dados.id)}>
                              CRIAR LOTE
                            </Link>
                          )}
                        </Typography>
                      </div>
                    </div>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>


            <SecaoGenerica titulo="Características Detalhadas e Entrada">
              <Grid container spacing={3}>
                <Grid item md={4} sm={6} xs={12}>
                  <Typography className={classes.metaLabel}>NÚMERO DE VESTÍGIO ASSOCIADO</Typography>
                  <Typography className={classes.metaValue}>{dados.numeroVestigioAssociado || '-'}</Typography>
                </Grid>

                <Grid item md={4} sm={6} xs={12}>
                  <Typography className={classes.metaLabel}>NATUREZA PRELIMINAR (GENÉRICA)</Typography>
                  <Typography className={classes.metaValue}>
                    {dados.tiposGens?.map((t) => t.nome?.toUpperCase()).join(', ') || 'NÃO INFORMADA'}
                  </Typography>
                </Grid>

                <Grid item md={4} sm={6} xs={12}>
                  <Typography className={classes.metaLabel}>NATUREZA PRELIMINAR (ESPECÍFICA)</Typography>
                  <Typography className={classes.metaValue}>
                    {dados.tiposEsps?.map((t) => t.nome?.toUpperCase()).join(', ') || 'NÃO INFORMADA'}
                  </Typography>
                </Grid>

                <Grid item md={4} sm={6} xs={12}>
                  <Typography className={classes.metaLabel}>TRATA-SE DE UMA CONTRAPROVA</Typography>
                  <Typography className={classes.metaValue}>{dados.contraprova ? 'SIM' : 'NÃO'}</Typography>
                </Grid>

                <Grid item md={4} sm={6} xs={12}>
                  <Typography className={classes.metaLabel}>TAMANHO DO OBJETO</Typography>
                  <Typography className={classes.metaValue}>{dados.tamanhoObjeto?.toUpperCase() || '-'}</Typography>
                </Grid>

                <Grid item md={4} sm={6} xs={12}>
                  <Typography className={classes.metaLabel}>SOLICITANTE DO CADASTRO</Typography>
                  <Typography className={classes.metaValue}>
                    {dados.usuSol || dados.usuarioSolicitanteInput
                      ? `${dados.usuSol?.nome?.toUpperCase() === 'OUTRO' ? dados.usuarioSolicitanteInput?.toUpperCase() || 'NÃO INFORMADO' : dados.usuSol?.nome?.toUpperCase()} ${dados.usuSol?.cargo ? '- ' + dados.usuSol.cargo.nome?.toUpperCase() : ''}`
                      : 'NÃO INFORMADO'}
                  </Typography>
                </Grid>

                <Grid item md={4} sm={6} xs={12}>
                  <Typography className={classes.metaLabel}>SETOR DE ENTRADA</Typography>
                  <Typography className={classes.metaValue}>
                    {dados.setRec ? `${dados.setRec.nome?.toUpperCase()} - ${dados.setRec.divisao?.sigla?.toUpperCase()} - ${dados.setRec.divisao?.regional?.nome?.toUpperCase()}` : '-'}
                  </Typography>
                </Grid>

                <Grid item md={4} sm={6} xs={12}>
                  <Typography className={classes.metaLabel}>DATA DE ENTRADA</Typography>
                  <Typography className={classes.metaValue}>{moment(dados.createdAt).format('LLLL').toUpperCase()}</Typography>
                </Grid>

                {dados.tipoEmbalagem && (
                  <Grid item md={4} sm={6} xs={12}>
                    <Typography className={classes.metaLabel}>EMBALAGEM</Typography>
                    <Typography className={classes.metaValue}>
                      TIPO: {dados.tipoEmbalagem === 'OUTRO' ? dados.outroTipoEmbalagem?.toUpperCase() : dados.tipoEmbalagem?.toUpperCase()}
                    </Typography>
                  </Grid>
                )}

                {dados.statusCadeiaCustodia === 'GUARDADO' && usuarioPertenceAoSetor && (
                  <Grid item md={4} sm={6} xs={12}>
                    <Typography className={classes.metaLabel}>LOCAL GUARDADO</Typography>
                    <Typography className={classes.metaValue}>
                      {ultimaOperacaoGuardar ? ultimaOperacaoGuardar.localizacao?.toUpperCase() : 'O VESTÍGIO NÃO ESTÁ GUARDADO'}
                    </Typography>
                  </Grid>
                )}

                {dados.endOrig && (
                  <Grid item xs={12}>
                    <Typography className={classes.metaLabel}>ENDEREÇO DE ORIGEM</Typography>
                    <Typography className={classes.metaValue}>
                      {`${dados.endOrig.rua ? dados.endOrig.rua.toUpperCase() : ''}, ${dados.endOrig.numero ? `${dados.endOrig.numero.toUpperCase()}, ` : ''} ${dados.endOrig.bairro ? `${dados.endOrig.bairro.toUpperCase()}, ` : ''} ${dados.endOrig.cidade ? dados.endOrig.cidade.toUpperCase() : ''
                        }, ${dados.endOrig.estado ? dados.endOrig.estado.toUpperCase() : ''}`}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" style={{ marginTop: '4px', display: 'block' }}>
                      {`Latitude: ${dados.endOrig.latitudeGps ? dados.endOrig.latitudeGps : 'não informada'}. Longitude: ${dados.endOrig.longitudeGps ? dados.endOrig.longitudeGps : 'não informada'}`}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </SecaoGenerica>


            <SecaoGenerica titulo="Objeto Origem">
              {cadaverOrigem || vestigioOrigem || veiculoOrigem || periciandoOrigem ? (
                <Grid container spacing={2}>
                  {cadaverOrigem && (
                    <Grid item md={3} sm={6} xs={12}>
                      <CardCadaver cadaver={cadaverOrigem} handleClick={handleClickCadaver} />
                    </Grid>
                  )}
                  {vestigioOrigem && (
                    <Grid item md={3} sm={6} xs={12}>
                      <CardVestigio vestigio={vestigioOrigem} handleClick={handleClickVestigio} />
                    </Grid>
                  )}
                  {veiculoOrigem && (
                    <Grid item md={3} sm={6} xs={12}>
                      <CardVeiculo veiculo={veiculoOrigem} handleClick={handleClickVeiculo} />
                    </Grid>
                  )}
                  {periciandoOrigem && (
                    <Grid item md={3} sm={6} xs={12}>
                      <CardPericiando periciando={periciandoOrigem} handleClick={handleClickPericiando} />
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Grid container justifyContent="center" alignItems="center" style={{ padding: '30px 0' }}>
                  <Typography variant="body2" color="textSecondary">
                    Este vestígio não foi extraído de um cadáver ou de outro vestígio. Trata-se de um vestígio origem.
                  </Typography>
                </Grid>
              )}
            </SecaoGenerica>


            <SecaoGenerica titulo="Casos Associados">
              {dados.casos && dados.casos.length ? (
                <Grid container spacing={2}>
                  {dados.casos.map((caso) => (
                    <Grid item md={3} sm={6} xs={12} key={caso.id}>
                      <CardCaso caso={caso} handleClick={handleClickCaso} />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Grid container justifyContent="center" alignItems="center" style={{ padding: '30px 0' }}>
                  <Typography variant="body2" color="textSecondary">Nenhum caso associado</Typography>
                </Grid>
              )}
            </SecaoGenerica>


            <SecaoGenerica titulo="Exames Solicitados">
              {dados.exSols && dados.exSols.length ? (
                <Grid container spacing={2}>
                  {dados.exSols.map((exameSolicitado) => (
                    <Grid item md={3} sm={6} xs={12} key={exameSolicitado.id}>
                      <CardExameSolicitado exameSolicitado={exameSolicitado} handleClick={handleClickExameSolicitado} />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Grid container justifyContent="center" alignItems="center" style={{ padding: '30px 0' }}>
                  <Typography variant="body2" color="textSecondary">Nenhum exame solicitado</Typography>
                </Grid>
              )}
            </SecaoGenerica>


            <SecaoGenerica titulo="Vestígios Extraídos">
              {vestigiosExtraidos && vestigiosExtraidos.length ? (
                <Grid container spacing={2}>
                  {vestigiosExtraidos.map((vest) => (
                    <Grid item md={3} sm={6} xs={12} key={vest.id}>
                      <CardVestigio vestigio={vest} handleClick={handleClickVestigio} />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Grid container justifyContent="center" alignItems="center" style={{ padding: '30px 0' }}>
                  <Typography variant="body2" color="textSecondary">Nenhum outro vestígio foi extraído deste</Typography>
                </Grid>
              )}
            </SecaoGenerica>


            <SecaoGenerica titulo="Termo de Não Conformidade">
              {termoNaoConformidade ? (
                <Grid container spacing={2}>
                  <Grid item md={4} sm={8} xs={12}>
                    <Card className={classes.card}>
                      <StyledMenu id="customized-menu" anchorEl={anchorElDocumento} keepMounted open={Boolean(anchorElDocumento)} onClose={handleCloseMenuDocumento}>
                        <StyledMenuItem onClick={handleClickOpenConfirmarIdentidade} disabled={!isUsuarioAssinante(usuarioState.id)}>
                          <ListItemIcon>
                            <CreateIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary="Assinar documento" />
                        </StyledMenuItem>
                        <StyledMenuItem onClick={handleDownloadTermoNaoConformidade} disabled={!(isTermoAssinado || (!isTermoAssinado && [termoNaoConformidade.testemunha?.id, termoNaoConformidade.perito?.id].includes(usuarioState.id)))}>
                          <ListItemIcon>
                            <CloudDownloadIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary="Download" />
                        </StyledMenuItem>
                      </StyledMenu>
                      <CardHeader
                        avatar={
                          <Avatar className={classes.avatar}>
                            <DescriptionIcon />
                          </Avatar>
                        }
                        title={<Typography variant="body1" style={{ fontWeight: 600 }}>Termo de Não Conformidade</Typography>}
                        style={{ borderBottom: 'thin solid rgba(0, 0, 0, 0.08)' }}
                        action={
                          <IconButton aria-label="settings" onClick={handleClickBotaoAcoesDocumento}>
                            <MoreVertIcon />
                          </IconButton>
                        }
                      />
                      <CardActionArea>
                        <CardContent className={classes.cardContent}>
                          <Typography variant="body2" style={{ marginBottom: '8px' }}>
                            <b>Não conformidade constatada: </b>
                            {termoNaoConformidade.nao_conformidade_constatada ? (termoNaoConformidade.nao_conformidade_constatada.length > 100 ? `${termoNaoConformidade.nao_conformidade_constatada.slice(0, 100)}...` : termoNaoConformidade.nao_conformidade_constatada) : '-'}
                          </Typography>
                          <Typography variant="body2" style={{ marginBottom: '8px' }}>
                            <b>Exame: </b> {termoNaoConformidade.exameSolicitado ? termoNaoConformidade.exameSolicitado.exReal?.numeroExame : '-'}
                          </Typography>
                          <Typography variant="body2" style={{ marginBottom: '8px' }}>
                            <b>Perito: </b> {termoNaoConformidade.perito ? termoNaoConformidade.perito.nome : '-'}
                          </Typography>
                          <Typography variant="body2">
                            <b>Testemunha: </b> {termoNaoConformidade.testemunha ? termoNaoConformidade.testemunha.nome : '-'}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                </Grid>
              ) : (
                <Grid container justifyContent="center" alignItems="center" style={{ padding: '30px 0' }}>
                  <Typography variant="body2" color="textSecondary">Não existe Termo de Não Conformidade referente a este vestígio.</Typography>
                </Grid>
              )}
            </SecaoGenerica>


            <SecaoGenerica titulo="Arquivos Relacionados">
              {dados.arqs && dados.arqs.length ? (
                <Grid container spacing={2}>
                  {dados.arqs.map((arquivo) => (
                    <Grid item md={2} sm={4} xs={6} key={arquivo.id} className={classes.item}>
                      <ExibirArquivo id={arquivo.id} rotaLoadFile="/arquivo/vestigio" />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Grid container justifyContent="center" alignItems="center" style={{ padding: '30px 0' }}>
                  <Typography variant="body2" color="textSecondary">Nenhum arquivo relacionado</Typography>
                </Grid>
              )}
            </SecaoGenerica>


            {entregasVestigio && entregasVestigio.length > 0 && (
              <SecaoGenerica titulo="Dados de Entrega do Vestígio">
                <Grid container spacing={3}>
                  {entregasVestigio.map((entrega, index) => (
                    <Grid item xs={12} key={entrega.id} className={classes.item}>
                      <Grid container spacing={2} className={classes.containerDadoEntrega}>
                        <Grid item md={6} sm={6} xs={12}>
                          <Typography className={classes.metaLabel}>DATA DA ENTREGA</Typography>
                          <Typography className={classes.metaValue}>{moment(entrega.createdAt).format('LLLL').toUpperCase()}</Typography>
                        </Grid>
                        <Grid item md={6} sm={6} xs={12}>
                          <Typography className={classes.metaLabel}>SETOR DESTINO</Typography>
                          <Typography className={classes.metaValue}>{entrega.setDest ? entrega.setDest.nome?.toUpperCase() : '-'}</Typography>
                        </Grid>
                        <Grid item md={6} sm={6} xs={12}>
                          <Typography className={classes.metaLabel}>USUÁRIO RESPONSÁVEL PELA ENTREGA</Typography>
                          <Typography className={classes.metaValue}>
                            {entrega.usu ? `${entrega.usu.nome?.toUpperCase()} - ${entrega.usu.matricula || 'SEM MATRÍCULA'}` : '-'}
                          </Typography>
                        </Grid>
                        <Grid item md={6} sm={6} xs={12}>
                          <Typography className={classes.metaLabel}>USUÁRIO RESPONSÁVEL PELO RECEBIMENTO</Typography>
                          <Typography className={classes.metaValue}>
                            {entrega.usuRec || entrega.usuarioRecebedorInput
                              ? `${entrega.usuRec.nome.toUpperCase() === 'OUTRO' ? entrega.usuarioRecebedorInput.toUpperCase() : entrega.usuRec.nome.toUpperCase()} - ${entrega.usuRec.nome.toUpperCase() === 'OUTRO' ? 'SEM MATRÍCULA' : entrega.usuRec.matricula || 'SEM MATRÍCULA'}`
                              : '-'}
                          </Typography>
                        </Grid>

                        {verificarPermissao([constants.PERMISSOES.LISTAR_VESTIGIOS]) && (
                          <Grid item xs={12} style={{ marginTop: '16px' }}>
                            <Typography className={classes.metaLabel}>DOCUMENTOS RELACIONADOS A ESTA ENTREGA</Typography>
                            {entrega.arquivos && entrega.arquivos.length > 0 ? (
                              <Grid container spacing={2} style={{ marginTop: '8px' }}>
                                {entrega.arquivos.map((arquivo) => (
                                  <Grid item md={2} sm={4} xs={6} key={arquivo.id}>
                                    <ExibirArquivo id={arquivo.id} rotaLoadFile="/arquivo/entregavestigio" />
                                  </Grid>
                                ))}
                              </Grid>
                            ) : (
                              <Typography variant="body2" color="textSecondary" style={{ marginTop: '8px' }}>
                                Nenhum arquivo relacionado com esta entrega
                              </Typography>
                            )}
                          </Grid>
                        )}
                      </Grid>
                      {index < entregasVestigio.length - 1 && <Divider style={{ margin: '24px 0' }} />}
                    </Grid>
                  ))}
                </Grid>
              </SecaoGenerica>
            )}

            <SecaoGenerica
              titulo="Ficha de Acompanhamento do Vestígio"
              rightAction={
                !isMobile && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Typography variant="caption" color="textSecondary">TIMELINE</Typography>
                    <Switch
                      checked={switchCadeiaCustodia}
                      onChange={handleChangeSwitchCadeiaCustodia}
                      color="primary"
                      name="timelimecheck"
                      inputProps={{ 'aria-label': 'Alternar visualização da cadeia de custódia' }}
                    />
                  </div>
                )
              }
            >
              <Grid item xs={12}>
                {isMobile && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', marginBottom: '16px', gap: '8px' }}>
                    <Typography variant="caption" color="textSecondary">TIMELINE</Typography>
                    <Switch
                      checked={switchCadeiaCustodia}
                      onChange={handleChangeSwitchCadeiaCustodia}
                      color="primary"
                      name="timelimecheck"
                      inputProps={{ 'aria-label': 'Alternar visualização da cadeia de custódia' }}
                    />
                  </div>
                )}
                {dados.id ? (
                  <div>
                    {!switchCadeiaCustodia && <FAV idVestigio={dados.id} />}
                    {switchCadeiaCustodia && <TimeLineCadeiaDeCustodia idVestigio={dados.id} />}
                  </div>
                ) : null}
              </Grid>
            </SecaoGenerica>


            <SecaoGenerica titulo="Informações Adicionadas">
              <Grid item xs={12}>
                <ListaInformacoes tipo="vestigio" id={dados.id} />
              </Grid>
            </SecaoGenerica>


            <SecaoGenerica titulo="Histórico de Operações">
              <Grid item xs={12} style={{ paddingTop: '10px' }}>
                {dados.id && <HistoricoOperacoes id={dados.id} tipo="VESTIGIO" />}
              </Grid>
            </SecaoGenerica>
          </>
        )}
      </div>

      <ConfirmarIdentidade
        aberto={openConfirmarIdentidade}
        setAberto={setOpenConfirmarIdentidade}
        callback={handleClickAssinarDocumento}
        titulo="Assinar Documento"
      />
    </Page>
  )
}
